import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 50;

export function useFeedPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchPage = useCallback(async (offset) => {
    const { data, error } = await supabase.rpc('get_feed_posts', {
      p_limit: PAGE_SIZE,
      p_offset: offset,
    });

    if (error) {
      console.error('useFeedPosts error:', error);
      return [];
    }

    return data ?? [];
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const data = await fetchPage(0);
      if (!cancelled) {
        setPosts(data);
        setHasMore(data.length === PAGE_SIZE);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fetchPage]);

  // Re-fetch page one and drop any pages beyond it. Pull-to-refresh and
  // app-resume both land here; `loading` deliberately stays false so the feed
  // never blanks out under the user mid-refresh.
  const refresh = useCallback(async () => {
    const data = await fetchPage(0);
    setPosts(data);
    setHasMore(data.length === PAGE_SIZE);
  }, [fetchPage]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    const data = await fetchPage(posts.length);
    setPosts(prev => [...prev, ...data]);
    setHasMore(data.length === PAGE_SIZE);
    setLoadingMore(false);
  }, [fetchPage, posts.length, loadingMore, hasMore]);

  return { posts, loading, loadingMore, hasMore, fetchMore, refresh };
}
